import React, { useState } from 'react';
import { TemplateType, Step } from '../../types/inAppMessageTypes';
import {
  Smartphone, Layout, Type, Users, ChevronRight, Check, Palette, MousePointerClick, Wand2, Loader,
  Home, MessageSquare, Calendar, Settings, PenSquare, QrCode, Users as UsersIcon, Shield, Heart, Info, Search,
} from 'lucide-react';
import { usePreferences } from '../../contexts/PreferencesContext';
import { getThemeColors } from '../../config/theme';
import inAppMessageService from '../../services/inAppMessage.service';
import {
  IN_APP_MESSAGE_DESTINATIONS,
  DEFAULT_DESTINATION_ID,
} from '../../constants/inAppMessageDestinations';

const DESTINATION_ICONS = {
  Home,
  MessageSquare,
  Calendar,
  Settings,
  PenSquare,
  QrCode,
  Users: UsersIcon,
  Shield,
  Heart,
  Info,
  Search,
};

// --- Reusable UI Components ---

const ColorInput = ({ label, value, onChange, colors }) => (
  <div>
    <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>{label}</label>
    <div className="flex items-center gap-3 p-2 rounded-lg border" style={{ backgroundColor: colors.backgroundSecondary, borderColor: colors.border }}>
      <div className="relative w-8 h-8 rounded-full shadow-sm border overflow-hidden flex-shrink-0 group" style={{ borderColor: colors.border }}>
        <div 
          className="absolute inset-0 z-0" 
          style={{ backgroundColor: value }}
        />
        <input 
          type="color" 
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="opacity-0 w-full h-full absolute inset-0 cursor-pointer z-10"
        />
      </div>
      <div className="flex-1 relative">
         <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs" style={{ color: colors.textTertiary }}>#</span>
         <input 
            type="text" 
            value={value.replace('#', '')}
            onChange={(e) => onChange('#' + e.target.value)}
            className="w-full border rounded text-xs font-mono py-1.5 pl-5 pr-2 uppercase focus:outline-none focus:ring-1"
            style={{ 
              backgroundColor: colors.background, 
              borderColor: colors.border, 
              color: colors.textPrimary
            }}
            onFocus={(e) => e.target.style.borderColor = colors.primary}
            onBlur={(e) => e.target.style.borderColor = colors.border}
          />
      </div>
    </div>
  </div>
);

// --- Sub-Components defined OUTSIDE to prevent re-renders ---

const ActionEditor = ({ config, onChange, colors, isDarkMode, t }) => {
  const action = config.content.primaryAction;
  const selectedDestinationId =
    action.destinationId ||
    (action.type === 'screen' ? DEFAULT_DESTINATION_ID : null);

  const handleTypeChange = (newType) => {
    if (newType === 'link') {
      const newValue =
        action.value && action.value.startsWith('http') ? action.value : 'https://';
      onChange('primaryAction', { type: 'link', value: newValue });
      return;
    }
    onChange('primaryAction', {
      type: 'screen',
      destinationId: action.destinationId || DEFAULT_DESTINATION_ID,
    });
  };

  const selectDestination = (destinationId) => {
    onChange('primaryAction', { type: 'screen', destinationId });
  };

  return (
    <div className="p-4 rounded-xl border space-y-4" style={{ backgroundColor: colors.backgroundSecondary, borderColor: colors.border }}>
       <div className="flex items-center gap-2 text-sm font-semibold pb-2 border-b" style={{ color: colors.textPrimary, borderColor: colors.border }}>
         <MousePointerClick size={16} style={{ color: colors.primary }} />
         {t('pages.inAppMessages.editor.actionEditor.title')}
       </div>
       
       <div className="grid grid-cols-2 gap-2 p-1 rounded-lg" style={{ backgroundColor: colors.backgroundTertiary }}>
          <button
            onClick={() => handleTypeChange('screen')}
            className="text-xs font-medium py-1.5 rounded-md transition-all"
            style={{
              backgroundColor: action.type === 'screen' ? colors.background : 'transparent',
              color: action.type === 'screen' ? colors.primary : colors.textSecondary,
              boxShadow: action.type === 'screen' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
            }}
            onMouseEnter={(e) => {
              if (action.type !== 'screen') {
                e.currentTarget.style.color = colors.textPrimary;
              }
            }}
            onMouseLeave={(e) => {
              if (action.type !== 'screen') {
                e.currentTarget.style.color = colors.textSecondary;
              }
            }}
          >
            {t('pages.inAppMessages.editor.actionEditor.inAppScreen')}
          </button>
          <button
            onClick={() => handleTypeChange('link')}
            className="text-xs font-medium py-1.5 rounded-md transition-all"
            style={{
              backgroundColor: action.type === 'link' ? colors.background : 'transparent',
              color: action.type === 'link' ? colors.primary : colors.textSecondary,
              boxShadow: action.type === 'link' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
            }}
            onMouseEnter={(e) => {
              if (action.type !== 'link') {
                e.currentTarget.style.color = colors.textPrimary;
              }
            }}
            onMouseLeave={(e) => {
              if (action.type !== 'link') {
                e.currentTarget.style.color = colors.textSecondary;
              }
            }}
          >
            {t('pages.inAppMessages.editor.actionEditor.externalLink')}
          </button>
       </div>

       {action.type === 'screen' ? (
         <div className="space-y-2">
           <div>
             <label className="block text-xs font-medium mb-1" style={{ color: colors.textSecondary }}>
               {t('pages.inAppMessages.editor.destinations.selectDestination')}
             </label>
             <p className="text-[10px] mb-2" style={{ color: colors.textTertiary }}>
               {t('pages.inAppMessages.editor.destinations.destinationHint')}
             </p>
           </div>
           <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-1">
             {IN_APP_MESSAGE_DESTINATIONS.map((dest) => {
               const isSelected = selectedDestinationId === dest.id;
               const Icon = DESTINATION_ICONS[dest.icon] || Home;
               return (
                 <button
                   key={dest.id}
                   type="button"
                   onClick={() => selectDestination(dest.id)}
                   className="flex items-center gap-3 p-3 rounded-lg border text-left transition-all"
                   style={{
                     borderColor: isSelected ? colors.primary : colors.border,
                     backgroundColor: isSelected
                       ? (isDarkMode ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.06)')
                       : colors.background,
                   }}
                 >
                   <div
                     className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                     style={{
                       backgroundColor: isSelected ? colors.primary : colors.backgroundTertiary,
                       color: isSelected ? '#fff' : colors.textSecondary,
                     }}
                   >
                     <Icon size={18} />
                   </div>
                   <div className="flex-1 min-w-0">
                     <div className="text-sm font-medium truncate" style={{ color: colors.textPrimary }}>
                       {t(`pages.inAppMessages.editor.destinations.${dest.id}.label`)}
                     </div>
                     <div className="text-xs truncate" style={{ color: colors.textTertiary }}>
                       {t(`pages.inAppMessages.editor.destinations.${dest.id}.desc`)}
                     </div>
                   </div>
                   {isSelected && <Check size={16} style={{ color: colors.primary, flexShrink: 0 }} />}
                 </button>
               );
             })}
           </div>
         </div>
       ) : (
         <div>
           <label className="block text-xs font-medium mb-1" style={{ color: colors.textSecondary }}>
             {t('pages.inAppMessages.editor.actionEditor.urlAddress')}
           </label>
           <input 
              type="text"
              value={action.value || ''}
              onChange={(e) => onChange('primaryAction', { type: 'link', value: e.target.value })}
              placeholder={t('pages.inAppMessages.editor.actionEditor.placeholderUrl')}
              className="w-full border rounded-lg p-2 text-sm focus:ring-2 outline-none"
              style={{ 
                backgroundColor: colors.background, 
                borderColor: colors.border, 
                color: colors.textPrimary
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = colors.primary}
              onBlur={(e) => e.currentTarget.style.borderColor = colors.border}
           />
         </div>
       )}
    </div>
  );
};

const TemplateSelector = ({ currentType, onSelect, colors, isDarkMode, t }) => (
  <div className="grid grid-cols-1 gap-4">
    {[
      { id: TemplateType.TOP_BANNER, labelKey: 'topBanner', descKey: 'topBannerDesc' },
      { id: TemplateType.CENTER_MODAL, labelKey: 'centerModal', descKey: 'centerModalDesc' },
      { id: TemplateType.FULL_SCREEN, labelKey: 'fullScreen', descKey: 'fullScreenDesc' },
    ].map((template) => {
      const isSelected = currentType === template.id;
      return (
        <button
          key={template.id}
          onClick={() => onSelect(template.id)}
          className="p-4 border-2 rounded-xl text-left transition-all"
          style={{
            borderColor: isSelected ? colors.primary : colors.border,
            backgroundColor: isSelected ? (isDarkMode ? 'rgba(37, 99, 235, 0.1)' : 'rgba(99, 102, 241, 0.05)') : colors.background,
            boxShadow: isSelected ? '0 4px 6px rgba(0,0,0,0.1)' : 'none'
          }}
          onMouseEnter={(e) => {
            if (!isSelected) {
              e.currentTarget.style.borderColor = colors.primary;
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected) {
              e.currentTarget.style.borderColor = colors.border;
            }
          }}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold" style={{ color: colors.textPrimary }}>{t(`pages.inAppMessages.editor.templateSelector.${template.labelKey}`)}</span>
            {isSelected && <Check size={18} style={{ color: colors.primary }} />}
          </div>
          <p className="text-sm" style={{ color: colors.textSecondary }}>{t(`pages.inAppMessages.editor.templateSelector.${template.descKey}`)}</p>
        </button>
      );
    })}
  </div>
);

const ContentEditor = ({ config, onChange, onChangeStyle, colors, isDarkMode, t, language }) => {
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleMagicFill = async () => {
    if (!aiPrompt.trim()) return;
    
    setIsGenerating(true);
    try {
      const result = await inAppMessageService.generateContent(aiPrompt.trim(), language);
      if (result && result.data) {
        const data = result.data;
        // Update content
        if (data.title) onChange('title', data.title);
        if (data.body) onChange('body', data.body);
        if (data.secondaryButtonText) onChange('secondaryButtonText', data.secondaryButtonText);
        
        // Only show secondary button (not primary) unless it's TOP_BANNER
        if (config.type !== TemplateType.TOP_BANNER) {
          onChange('showPrimaryButton', false);
          onChange('showSecondaryButton', true);
        }
        
        // Update image URL if provided
        if (data.imageUrl) {
          onChange('imageUrl', data.imageUrl);
          onChange('showImage', true);
        }
        
        // Update styles if provided
        if (data.suggestedBackgroundColor) {
          onChangeStyle('backgroundColor', data.suggestedBackgroundColor);
        }
        if (data.suggestedTextColor) {
          onChangeStyle('textColor', data.suggestedTextColor);
        }
        if (data.suggestedButtonColor) {
          onChangeStyle('buttonColor', data.suggestedButtonColor);
        }
        if (data.suggestedSecondaryButtonTextColor) {
          onChangeStyle('secondaryButtonTextColor', data.suggestedSecondaryButtonTextColor);
        }
        
        setAiPrompt(''); // Clear prompt after success
      }
    } catch (error) {
      console.error('Error generating content:', error);
      alert(error.response?.data?.message || t('pages.inAppMessages.errors.generatingContent'));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.contentEditor.title')}</label>
          <input 
            type="text" 
            value={config.content.title} 
            onChange={(e) => onChange('title', e.target.value)}
            className="w-full border rounded-lg p-2.5 focus:ring-2 outline-none transition-shadow"
            style={{ 
              backgroundColor: colors.background, 
              borderColor: colors.border, 
              color: colors.textPrimary
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = colors.primary}
            onBlur={(e) => e.currentTarget.style.borderColor = colors.border}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.contentEditor.body')}</label>
          <textarea 
            rows={3}
            value={config.content.body} 
            onChange={(e) => onChange('body', e.target.value)}
            className="w-full border rounded-lg p-2.5 focus:ring-2 outline-none transition-shadow"
            style={{ 
              backgroundColor: colors.background, 
              borderColor: colors.border, 
              color: colors.textPrimary
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = colors.primary}
            onBlur={(e) => e.currentTarget.style.borderColor = colors.border}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
             <label className="text-sm font-medium" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.contentEditor.imageUrl')}</label>
             <div className="flex items-center gap-2">
               <span className="text-xs" style={{ color: colors.textSecondary }}>{t('pages.inAppMessages.editor.contentEditor.showImage')}</span>
               <input 
                type="checkbox"
                checked={config.content.showImage}
                onChange={(e) => onChange('showImage', e.target.checked)}
                className="w-4 h-4 rounded"
                style={{ accentColor: colors.primary }}
              />
             </div>
          </div>
          <input 
            type="text" 
            value={config.content.imageUrl || ''} 
            disabled={!config.content.showImage}
            onChange={(e) => onChange('imageUrl', e.target.value)}
            placeholder="https://..."
            className="w-full border rounded-lg p-2.5 text-sm focus:ring-2 outline-none"
            style={{ 
              backgroundColor: colors.background, 
              borderColor: colors.border, 
              color: colors.textPrimary,
              opacity: config.content.showImage ? 1 : 0.5
            }}
            onFocus={(e) => {
              if (config.content.showImage) {
                e.currentTarget.style.borderColor = colors.primary;
              }
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = colors.border;
            }}
          />
        </div>
        
        {/* Buttons Config - Hidden for TOP_BANNER */}
        {config.type !== TemplateType.TOP_BANNER && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.contentEditor.primaryButton')}</label>
                  <input 
                    type="checkbox"
                    checked={config.content.showPrimaryButton}
                    onChange={(e) => onChange('showPrimaryButton', e.target.checked)}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: colors.primary }}
                  />
                </div>
                <input 
                  type="text" 
                  value={config.content.buttonText} 
                  disabled={!config.content.showPrimaryButton}
                  onChange={(e) => onChange('buttonText', e.target.value)}
                  className="w-full border rounded-lg p-2.5 focus:ring-2 outline-none"
                  style={{ 
                    backgroundColor: colors.background, 
                    borderColor: colors.border, 
                    color: colors.textPrimary,
                    opacity: config.content.showPrimaryButton ? 1 : 0.5
                  }}
                  onFocus={(e) => {
                    if (config.content.showPrimaryButton) {
                      e.currentTarget.style.borderColor = colors.primary;
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                  }}
                />
              </div>
              <div>
                 <div className="flex items-center justify-between mb-2">
                   <label className="text-sm font-medium" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.contentEditor.secondaryButton')}</label>
                   <input 
                    type="checkbox"
                    checked={config.content.showSecondaryButton}
                    disabled={config.type === TemplateType.FULL_SCREEN}
                    onChange={(e) => {
                      // Don't allow disabling secondary button for FULL_SCREEN
                      if (config.type === TemplateType.FULL_SCREEN) return;
                      onChange('showSecondaryButton', e.target.checked);
                    }}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: colors.primary, opacity: config.type === TemplateType.FULL_SCREEN ? 0.5 : 1 }}
                  />
                 </div>
                 <input 
                   type="text" 
                   value={config.content.secondaryButtonText} 
                   disabled={!config.content.showSecondaryButton || config.type === TemplateType.FULL_SCREEN}
                   onChange={(e) => {
                     // Don't allow changing text for FULL_SCREEN
                     if (config.type === TemplateType.FULL_SCREEN) return;
                     onChange('secondaryButtonText', e.target.value);
                   }}
                   placeholder={t('common.close')}
                   className="w-full border rounded-lg p-2.5 focus:ring-2 outline-none"
                   style={{ 
                     backgroundColor: colors.background, 
                     borderColor: colors.border, 
                     color: colors.textPrimary,
                     opacity: (config.content.showSecondaryButton && config.type !== TemplateType.FULL_SCREEN) ? 1 : 0.5
                   }}
                   onFocus={(e) => {
                     if (config.content.showSecondaryButton && config.type !== TemplateType.FULL_SCREEN) {
                       e.currentTarget.style.borderColor = colors.primary;
                     }
                   }}
                   onBlur={(e) => {
                     e.currentTarget.style.borderColor = colors.border;
                   }}
                 />
                 <p className="text-[10px] mt-1.5 leading-tight" style={{ color: colors.textTertiary }}>
                   {config.type === TemplateType.FULL_SCREEN 
                     ? t('pages.inAppMessages.editor.contentEditor.fullScreenSecondaryHint')
                     : t('pages.inAppMessages.editor.contentEditor.secondaryButtonHint')}
                 </p>
               </div>
            </div>
            
            {/* Primary Action Editor */}
            {config.content.showPrimaryButton && (
              <ActionEditor config={config} onChange={onChange} colors={colors} isDarkMode={isDarkMode} t={t} />
            )}
          </>
        )}
        
        {/* Action Editor for TOP_BANNER - whole content is clickable */}
        {config.type === TemplateType.TOP_BANNER && (
          <ActionEditor config={config} onChange={onChange} colors={colors} isDarkMode={isDarkMode} t={t} />
        )}
      </div>

      {/* AI Magic Generator - at the end */}
      <div className="mt-6 p-4 rounded-xl border" style={{ backgroundColor: colors.backgroundSecondary, borderColor: colors.border }}>
        <div className="flex items-center gap-2 mb-3">
          <Wand2 size={16} style={{ color: colors.primary }} />
          <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.contentEditor.magicGenerator')}</h3>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder={t('pages.inAppMessages.editor.contentEditor.magicPromptPlaceholder')}
            disabled={isGenerating}
            className="flex-1 border rounded-lg p-2 text-sm focus:ring-2 outline-none"
            style={{ 
              backgroundColor: colors.background, 
              borderColor: colors.border, 
              color: colors.textPrimary,
              opacity: isGenerating ? 0.5 : 1
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = colors.primary}
            onBlur={(e) => e.currentTarget.style.borderColor = colors.border}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isGenerating) {
                handleMagicFill();
              }
            }}
          />
          <button
            onClick={handleMagicFill}
            disabled={isGenerating || !aiPrompt.trim()}
            className="px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              backgroundColor: colors.primary,
              color: '#ffffff',
              minWidth: '44px',
              minHeight: '44px'
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.backgroundColor = colors.primaryHover;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary;
            }}
            title={isGenerating ? t('pages.inAppMessages.editor.contentEditor.generating') : t('pages.inAppMessages.editor.contentEditor.autoFill')}
          >
            {isGenerating ? (
              <Wand2 size={20} className="animate-spin" style={{ animationDuration: '1s' }} />
            ) : (
              <Wand2 size={20} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const StyleEditor = ({ config, onChange, colors, isDarkMode, t }) => (
  <div className="space-y-8">
    <div className="space-y-4">
      <h3 className="font-semibold border-b pb-2 flex items-center gap-2" style={{ color: colors.textPrimary, borderColor: colors.border }}>
        <Palette size={16} /> {t('pages.inAppMessages.editor.styleEditor.colorPalette')}
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <ColorInput 
          label={t('pages.inAppMessages.editor.styleEditor.background')} 
          value={config.styles.backgroundColor} 
          onChange={(v) => onChange('backgroundColor', v)} 
          colors={colors}
        />
        <ColorInput 
          label={t('pages.inAppMessages.editor.styleEditor.textColor')} 
          value={config.styles.textColor} 
          onChange={(v) => onChange('textColor', v)} 
          colors={colors}
        />
        
        {config.content.showPrimaryButton && (
          <>
            <ColorInput 
              label={t('pages.inAppMessages.editor.styleEditor.primaryBtnBg')} 
              value={config.styles.buttonColor} 
              onChange={(v) => onChange('buttonColor', v)} 
              colors={colors}
            />
            <ColorInput 
              label={t('pages.inAppMessages.editor.styleEditor.primaryBtnText')} 
              value={config.styles.buttonTextColor} 
              onChange={(v) => onChange('buttonTextColor', v)} 
              colors={colors}
            />
          </>
        )}
        
        {config.content.showSecondaryButton && config.type !== TemplateType.TOP_BANNER && (
          <>
            <ColorInput 
              label={t('pages.inAppMessages.editor.styleEditor.secondaryBtnBg')} 
              value={config.styles.secondaryButtonColor} 
              onChange={(v) => onChange('secondaryButtonColor', v)} 
              colors={colors}
            />
            <ColorInput 
              label={t('pages.inAppMessages.editor.styleEditor.secondaryBtnText')} 
              value={config.styles.secondaryButtonTextColor} 
              onChange={(v) => onChange('secondaryButtonTextColor', v)} 
              colors={colors}
            />
          </>
        )}

        {config.type === TemplateType.CENTER_MODAL && (
          <ColorInput 
            label={t('pages.inAppMessages.editor.styleEditor.overlayColor')} 
            value={config.styles.overlayColor.substring(0, 7)} 
            onChange={(v) => onChange('overlayColor', v + '99')} 
            colors={colors}
          />
        )}
      </div>
    </div>

    <div className="space-y-4">
         <h3 className="font-semibold border-b pb-2 flex items-center gap-2" style={{ color: colors.textPrimary, borderColor: colors.border }}>
            <Type size={16} /> {t('pages.inAppMessages.editor.styleEditor.typography')}
         </h3>
         <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.styleEditor.titleSize')}</label>
                <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textSecondary }}>{config.styles.titleFontSize}px</span>
              </div>
              <input 
                type="range" 
                min="16" 
                max="48" 
                value={config.styles.titleFontSize}
                onChange={(e) => onChange('titleFontSize', parseInt(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{ backgroundColor: colors.backgroundTertiary, accentColor: colors.primary }}
              />
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.styleEditor.bodySize')}</label>
                <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textSecondary }}>{config.styles.bodyFontSize}px</span>
              </div>
              <input 
                type="range" 
                min="12" 
                max="24" 
                value={config.styles.bodyFontSize}
                onChange={(e) => onChange('bodyFontSize', parseInt(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{ backgroundColor: colors.backgroundTertiary, accentColor: colors.primary }}
              />
            </div>
         </div>
    </div>

    {(config.type === TemplateType.TOP_BANNER || config.type === TemplateType.CENTER_MODAL) && (
      <div className="space-y-4">
         <h3 className="font-semibold border-b pb-2 flex items-center gap-2" style={{ color: colors.textPrimary, borderColor: colors.border }}>
            <Layout size={16} /> {t('pages.inAppMessages.editor.styleEditor.layout')}
         </h3>
         <div>
           <div className="flex justify-between mb-2">
            <label className="text-sm font-medium" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.styleEditor.cornerRadius')}</label>
            <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textSecondary }}>{config.styles.borderRadius}px</span>
           </div>
           <input 
             type="range" 
             min="0" 
             max="32" 
             value={config.styles.borderRadius}
             onChange={(e) => onChange('borderRadius', parseInt(e.target.value))}
             className="w-full h-2 rounded-lg appearance-none cursor-pointer"
             style={{ backgroundColor: colors.backgroundTertiary, accentColor: colors.primary }}
           />
         </div>
      </div>
    )}

    {config.content.showImage && config.type === TemplateType.TOP_BANNER && (
      <div className="space-y-4">
         <h3 className="font-semibold border-b pb-2 flex items-center gap-2" style={{ color: colors.textPrimary, borderColor: colors.border }}>
            <Palette size={16} /> {t('pages.inAppMessages.editor.styleEditor.image')}
         </h3>
         <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.styleEditor.imageSize')}</label>
                <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textSecondary }}>{config.styles.imageSize || 48}px</span>
              </div>
              <input 
                type="range" 
                min="32" 
                max="128" 
                value={config.styles.imageSize || 48}
                onChange={(e) => onChange('imageSize', parseInt(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{ backgroundColor: colors.backgroundTertiary, accentColor: colors.primary }}
              />
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: colors.textPrimary }}>{t('pages.inAppMessages.editor.styleEditor.imageBorderRadius')}</label>
                <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textSecondary }}>{config.styles.imageBorderRadius || 24}px</span>
              </div>
              <input 
                type="range" 
                min="0" 
                max="64" 
                value={config.styles.imageBorderRadius || 24}
                onChange={(e) => onChange('imageBorderRadius', parseInt(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{ backgroundColor: colors.backgroundTertiary, accentColor: colors.primary }}
              />
            </div>
         </div>
      </div>
    )}
  </div>
);

// --- Main Component ---

export const EditorPanel = ({ config, setConfig, currentStep, setStep, previousButtonStates, setPreviousButtonStates }) => {
  const { isDarkMode, t, language } = usePreferences();
  const colors = getThemeColors(isDarkMode);

  const updateContent = (key, value) => {
    setConfig(prev => ({
      ...prev,
      content: { ...prev.content, [key]: value }
    }));
  };

  const updateStyle = (key, value) => {
    setConfig(prev => ({
      ...prev,
      styles: { ...prev.styles, [key]: value }
    }));
  };

  return (
    <div className="flex flex-col h-full md:border-r shadow-sm" style={{ backgroundColor: colors.background, borderColor: colors.border }}>
      {/* Stepper Navigation */}
      <div className="flex border-b overflow-x-auto no-scrollbar sticky top-0 z-10" style={{ borderColor: colors.border, backgroundColor: colors.background }}>
        {[
          { id: 'template', icon: Layout, labelKey: 'template' },
          { id: 'content', icon: Type, labelKey: 'content' },
          { id: 'style', icon: Smartphone, labelKey: 'style' },
          { id: 'audience', icon: Users, labelKey: 'audience' },
        ].map((step) => {
          const isActive = currentStep === step.id;
          const Icon = step.icon;
          return (
            <button
              key={step.id}
              onClick={() => setStep(step.id)}
              className="flex-1 py-3 px-4 min-w-[70px] flex flex-col items-center justify-center gap-1 text-[10px] font-medium transition-colors border-b-2 border-transparent"
              style={{
                color: isActive ? colors.primary : colors.textTertiary,
                borderBottomColor: isActive ? colors.primary : 'transparent',
                backgroundColor: isActive ? (isDarkMode ? 'rgba(37, 99, 235, 0.1)' : 'rgba(99, 102, 241, 0.05)') : 'transparent'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = colors.textSecondary;
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = colors.textTertiary;
                }
              }}
            >
              <Icon size={18} />
              {t(`pages.inAppMessages.editor.steps.${step.labelKey}`)}
            </button>
          );
        })}
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {currentStep === 'template' && (
          <TemplateSelector 
            currentType={config.type} 
            onSelect={(type) => {
              const previousType = config.type;
              
              // Save current button states before switching
              if (previousType && previousType !== type) {
                setPreviousButtonStates(prev => ({
                  ...prev,
                  [previousType]: {
                    showPrimaryButton: config.content.showPrimaryButton,
                    showSecondaryButton: config.content.showSecondaryButton,
                    buttonText: config.content.buttonText,
                    secondaryButtonText: config.content.secondaryButtonText
                  }
                }));
              }
              
              const newConfig = { ...config, type };
              
              // Apply template-specific rules only when switching TO a template
              if (type === TemplateType.FULL_SCREEN) {
                // FULL_SCREEN always has secondary button "Close"
                newConfig.content = {
                  ...newConfig.content,
                  showSecondaryButton: true,
                  secondaryButtonText: t('pages.inAppMessages.defaults.secondaryButtonText')
                };
              } else if (type === TemplateType.TOP_BANNER) {
                // TOP_BANNER has no buttons (whole content is clickable)
                newConfig.content = {
                  ...newConfig.content,
                  showPrimaryButton: false,
                  showSecondaryButton: false
                };
              } else {
                // For CENTER_MODAL, restore previous state if available, otherwise use defaults
                const savedState = previousButtonStates[type];
                if (savedState) {
                  newConfig.content = {
                    ...newConfig.content,
                    showPrimaryButton: savedState.showPrimaryButton !== undefined ? savedState.showPrimaryButton : true,
                    showSecondaryButton: savedState.showSecondaryButton !== undefined ? savedState.showSecondaryButton : true,
                    buttonText: savedState.buttonText || newConfig.content.buttonText,
                    secondaryButtonText: savedState.secondaryButtonText || newConfig.content.secondaryButtonText
                  };
                } else {
                  // Default state for CENTER_MODAL
                  newConfig.content = {
                    ...newConfig.content,
                    showPrimaryButton: newConfig.content.showPrimaryButton !== undefined ? newConfig.content.showPrimaryButton : true,
                    showSecondaryButton: newConfig.content.showSecondaryButton !== undefined ? newConfig.content.showSecondaryButton : true,
                  };
                }
              }
              
              setConfig(newConfig);
            }} 
            colors={colors}
            isDarkMode={isDarkMode}
            t={t}
          />
        )}
        
        {currentStep === 'content' && (
          <ContentEditor 
            config={config} 
            onChange={updateContent}
            onChangeStyle={updateStyle}
            colors={colors}
            isDarkMode={isDarkMode}
            t={t}
            language={language}
          />
        )}

        {currentStep === 'style' && (
          <StyleEditor 
            config={config} 
            onChange={updateStyle} 
            colors={colors}
            isDarkMode={isDarkMode}
            t={t}
          />
        )}

      </div>

      {/* Footer Navigation */}
      <div className="p-4 border-t" style={{ borderColor: colors.border, backgroundColor: colors.backgroundSecondary }}>
        {currentStep !== 'audience' ? (
          <button 
            onClick={() => {
              const order = ['template', 'content', 'style', 'audience'];
              const next = order[order.indexOf(currentStep) + 1];
              if (next) setStep(next);
            }}
            className="w-full flex items-center justify-center gap-2 text-white py-3 rounded-lg font-medium transition-colors shadow-lg active:scale-[0.98]"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary;
            }}
          >
            {t('pages.inAppMessages.editor.footer.nextStep')} <ChevronRight size={16} />
          </button>
        ) : (
           <div className="text-center text-xs" style={{ color: colors.textTertiary }}>
             {t('pages.inAppMessages.editor.footer.selectAudience')}
           </div>
        )}
      </div>
    </div>
  );
};
